/**
 * Parser for Jira board URLs pasted by admins onboarding a new project.
 *
 * Canonical (Atlassian Cloud, current UI):
 *   https://<tenant>.atlassian.net/jira/software/c/projects/<KEY>/boards/<id>
 *   https://<tenant>.atlassian.net/jira/software/projects/<KEY>/boards/<id>      (no `/c/`)
 *
 * Legacy (RapidBoard) is rejected with an actionable message instead of
 * silently parsed — the project_key is not always recoverable from those URLs
 * and we'd rather force the admin to grab the modern link.
 */

/**
 * `project_key` is captured from the URL and surfaced as informational
 * ("owning project per Jira"), but is NOT part of the onboard kickoff body.
 * Per docs/architecture/board-keyed-scorecard.md the inner flow derives
 * project attribution from issue contents at runtime.
 */
export interface ParsedBoardUrl {
  jira_base_url: string;
  board_id: number;
  /** From the URL path; for display only — do not send to the API. */
  owning_project_per_jira: string;
}

export type ParseResult =
  | { ok: true; value: ParsedBoardUrl }
  | { ok: false; error: string };

const TENANT_HOST_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.atlassian\.net$/i;
const PROJECT_KEY_RE = /^[A-Z][A-Z0-9_]{0,29}$/;

const LEGACY_PATHS = ["/secure/RapidBoard.jspa", "/issues/?jql"];

export function parseBoardUrl(input: string): ParseResult {
  const raw = (input || "").trim();
  if (!raw) {
    return { ok: false, error: "Paste a Jira board URL." };
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { ok: false, error: "That doesn't look like a URL." };
  }

  if (url.protocol !== "https:") {
    return { ok: false, error: "URL must be https." };
  }

  if (!TENANT_HOST_RE.test(url.hostname)) {
    return {
      ok: false,
      error: "Host must be a *.atlassian.net Jira tenant.",
    };
  }

  for (const legacy of LEGACY_PATHS) {
    if (url.pathname.startsWith(legacy)) {
      return {
        ok: false,
        error:
          "Legacy URL — open the board in Jira and copy the modern URL " +
          "(/jira/software/c/projects/<KEY>/boards/<id>).",
      };
    }
  }

  // Path shapes:
  //   /jira/software/c/projects/<KEY>/boards/<id>            (current)
  //   /jira/software/projects/<KEY>/boards/<id>              (older, no `/c/`)
  //   .../boards/<id>/backlog, /timeline, /reports/burnup …  (board tabs)
  const re = /^\/jira\/software\/(?:c\/)?projects\/([^/]+)\/boards\/(\d+)(?:\/.*)?$/;
  const m = re.exec(url.pathname);
  if (!m) {
    return {
      ok: false,
      error:
        "Path must look like /jira/software/c/projects/<KEY>/boards/<id>.",
    };
  }

  const projectKey = m[1].toUpperCase();
  if (!PROJECT_KEY_RE.test(projectKey)) {
    return {
      ok: false,
      error: `Project key "${m[1]}" is not a valid Jira key (A-Z, A-Z0-9_, max 30 chars).`,
    };
  }

  const boardId = Number.parseInt(m[2], 10);
  if (!Number.isFinite(boardId) || boardId <= 0) {
    return { ok: false, error: `Board id "${m[2]}" is not a positive integer.` };
  }

  return {
    ok: true,
    value: {
      jira_base_url: `${url.protocol}//${url.hostname}`,
      board_id: boardId,
      owning_project_per_jira: projectKey,
    },
  };
}

/**
 * Onboarding input can be a board URL (one specific board, as before) or a
 * project reference (bare key, project URL, /browse URL) — the latter routes
 * the admin through board discovery + picker instead of direct onboarding.
 * Board URLs take priority: anything containing /boards/<id> stays kind=board.
 */
export type OnboardInput =
  | { kind: "board"; value: ParsedBoardUrl }
  | { kind: "project"; project_key: string };

export type OnboardParseResult =
  | { ok: true; input: OnboardInput }
  | { ok: false; error: string };

const PROJECT_URL_RE = /^\/jira\/software\/(?:c\/)?projects\/([^/]+)(?:\/|$)/;
const BROWSE_URL_RE = /^\/browse\/([A-Za-z][A-Za-z0-9_]*?)(?:-\d+)?\/?$/;

export function parseOnboardInput(input: string): OnboardParseResult {
  const raw = (input || "").trim();
  if (!raw) {
    return { ok: false, error: "Paste a Jira board/project URL or a project key." };
  }

  // Bare project key — no slashes, no dots, so it can't be a URL.
  if (!raw.includes("/") && !raw.includes(".")) {
    const key = raw.toUpperCase();
    if (PROJECT_KEY_RE.test(key)) {
      return { ok: true, input: { kind: "project", project_key: key } };
    }
    return {
      ok: false,
      error: `"${raw}" is not a valid Jira project key (A-Z, A-Z0-9_, max 30 chars).`,
    };
  }

  // Board URL takes priority over project interpretation.
  const board = parseBoardUrl(raw);
  if (board.ok) {
    return { ok: true, input: { kind: "board", value: board.value } };
  }
  // Legacy guidance is more useful than the generic fallthrough below.
  if (board.error.startsWith("Legacy URL")) {
    return { ok: false, error: board.error };
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { ok: false, error: board.error };
  }
  if (url.protocol !== "https:" || !TENANT_HOST_RE.test(url.hostname)) {
    return { ok: false, error: board.error };
  }

  const m = PROJECT_URL_RE.exec(url.pathname) ?? BROWSE_URL_RE.exec(url.pathname);
  if (m) {
    const key = m[1].toUpperCase();
    if (PROJECT_KEY_RE.test(key)) {
      return { ok: true, input: { kind: "project", project_key: key } };
    }
    return {
      ok: false,
      error: `Project key "${m[1]}" is not a valid Jira key (A-Z, A-Z0-9_, max 30 chars).`,
    };
  }

  return {
    ok: false,
    error: "Paste a board URL, a project URL, or a bare project key (e.g. DA).",
  };
}
